/**
 * useAdsTimer — Motor rAF para o ciclo de Stories da AdsSidebar.
 *
 * Responsabilidades:
 *  • Trocar a src dos elementos <img>/<video> via ref (sem remontagem)
 *  • Calcular progress (0→1) sincronizado com requestAnimationFrame
 *  • Capturar a duração real do vídeo via loadedmetadata (substitui duracao_segundos)
 *  • Avançar para o próximo item ou encerrar o ciclo via callbacks estáveis
 *
 * Garantias de blindagem para TV 24/7:
 *  • metadataGuard: fallback de 3 s caso loadedmetadata nunca dispare (codec incompatível)
 *  • blobMap → URL de rede: a sidebar não fica preta se o blob expirou
 *  • onCicloRef / onNextRef: stale closure prevention sem recriação de closures do rAF
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type React from 'react';
import type { Midia } from '@/shared/types';

export interface UseAdsTimerOptions {
  midias:           Midia[];
  idx:              number;
  blobMap:          ReadonlyMap<string, string>;
  videoRef:         React.RefObject<HTMLVideoElement | null>;
  imgRef:           React.RefObject<HTMLImageElement | null>;
  /** Chamado com o índice do próximo item quando o tempo da mídia atual expira. */
  onNext:           (nextIdx: number) => void;
  /** Chamado quando o último item do ciclo expira. */
  onCicloCompleto?: () => void;
}

export interface UseAdsTimerReturn {
  /** Progresso da mídia atual: 0.0 a 1.0, atualizado a cada frame via rAF. */
  progress:             number;
  /** Handler para onLoadedMetadata do <video> — captura a duração real do arquivo. */
  handleLoadedMetadata: () => void;
}

export function useAdsTimer({
  midias,
  idx,
  blobMap,
  videoRef,
  imgRef,
  onNext,
  onCicloCompleto,
}: UseAdsTimerOptions): UseAdsTimerReturn {
  const [progress, setProgress] = useState(0);

  const rafRef     = useRef<number>(0);
  // performance.now() do instante em que a mídia atual começou a contar
  const startTs    = useRef<number>(0);
  // Duração real em ms — 0 enquanto aguarda loadedmetadata do vídeo
  const durMs      = useRef<number>(0);

  // Refs estáveis para callbacks: isolam o ciclo de vida do rAF das re-renderizações
  const onCicloRef = useRef(onCicloCompleto);
  const onNextRef  = useRef(onNext);
  useEffect(() => { onCicloRef.current = onCicloCompleto; }, [onCicloCompleto]);
  useEffect(() => { onNextRef.current  = onNext;          }, [onNext]);

  // Captura a duração real do vídeo e zera o timer a partir desse instante.
  // Invocado pelo evento onLoadedMetadata do <video> no MediaContent.
  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isFinite(video.duration) || video.duration <= 0) return;
    durMs.current   = video.duration * 1000;
    startTs.current = performance.now(); // timer começa no frame real, não no .src=
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const midia = midias[idx] ?? null;
    if (midias.length === 0 || !midia) return;

    cancelAnimationFrame(rafRef.current);
    setProgress(0);

    // Blob URL pré-cacheado → fallback URL de rede (sidebar nunca fica preta)
    const src = blobMap.get(midia.url) ?? midia.url;

    if (midia.tipo === 'video') {
      // durMs = 0 mantém o rAF em espera até loadedmetadata fornecer duração real
      durMs.current = 0;
      if (imgRef.current)   imgRef.current.src = '';
      if (videoRef.current) {
        videoRef.current.src = src;
        videoRef.current.load();
        videoRef.current.play().catch(() => {});
      }
    } else {
      durMs.current = (midia.duracao_segundos ?? 10) * 1000;
      if (videoRef.current) { videoRef.current.pause(); videoRef.current.src = ''; }
      if (imgRef.current)   imgRef.current.src = src;
    }

    startTs.current = performance.now();

    // Guard: arquivo corrompido ou codec não suportado pode nunca disparar loadedmetadata.
    // Após 3 s sem duração real, usa duracao_segundos do banco como fallback.
    const metadataGuard = midia.tipo === 'video'
      ? setTimeout(() => {
          if (durMs.current === 0) {
            durMs.current   = (midia.duracao_segundos ?? 10) * 1000;
            startTs.current = performance.now();
          }
        }, 3_000)
      : null;

    function tick(now: number) {
      // Spin enquanto aguarda a duração real do vídeo
      if (durMs.current === 0) { rafRef.current = requestAnimationFrame(tick); return; }

      const pct = Math.min((now - startTs.current) / durMs.current, 1);
      setProgress(pct);

      if (pct < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        const next = idx + 1;
        if (next >= midias.length) {
          onCicloRef.current?.();
        } else {
          onNextRef.current(next);
        }
      }
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      if (metadataGuard) clearTimeout(metadataGuard);
    };
  }, [idx, midias, blobMap]); // eslint-disable-line react-hooks/exhaustive-deps
  // videoRef e imgRef são refs estáveis — não entram nos deps para não recriar closures rAF

  return { progress, handleLoadedMetadata };
}
